import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';
import { apiRequest } from '../../config/api';
import { Trash2, Plus, X } from 'lucide-react';

/* =========================================================
   ProcedureTrackingForm
   - Local draft autosave (localStorage)
   - Inline per-row validation
   - Cleaned payload passed to onSave/onNext
   ========================================================= */

const DRAFT_KEY_BASE = "dental:procedureTracking";

const emptyRow = () => {
  // Get current date in YYYY-MM-DD format for date input
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const currentDate = `${year}-${month}-${day}`;
  
  return {
    visitDate: currentDate,
    procedure: "",
    nextApptDate: "",
    total: "",
    paid: "",
    due: 0,
  };
};

const clampNum = (n) => (Number.isFinite(n) && n >= 0 ? n : 0);
const parseNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const hasValue = (v) => String(v ?? "").trim() !== "";

const inr = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    .replace("₹", "₹ ");

const ProcedureTrackingForm = (props) => {
  // Destructure props with defaults
  const {
    hideDialog = false,
    open,
    onClose,
    initial = {},
    patientId,
    patientName,
    visitId,
    medicalHistory,
    dentalExamination,
    onBack,
    onNext,
    onSave,
  } = props || {};
  
  // Ensure hideDialog is a boolean
  const isHideDialog = hideDialog === true;
  // Per-visit draft key so forms don't collide between patients/visits
  const DRAFT_KEY = useMemo(
    () => `${DRAFT_KEY_BASE}:${visitId || patientId || "new"}:draft`,
    [visitId, patientId]
  );

  // Calculate date constraints: today and 3 months from today
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMaxDate = () => {
    const today = new Date();
    const threeMonthsLater = new Date(today);
    threeMonthsLater.setMonth(today.getMonth() + 3);
    const year = threeMonthsLater.getFullYear();
    const month = String(threeMonthsLater.getMonth() + 1).padStart(2, '0');
    const day = String(threeMonthsLater.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const minDate = getTodayDate();
  const maxDate = getMaxDate();

  const [rows, setRows] = useState(
    initial.rows?.length
      ? initial.rows.map((r) => ({
          ...r,
          due: clampNum(parseNum(r.total) - parseNum(r.paid)),
        }))
      : [emptyRow()]
  );
  const [errors, setErrors] = useState([]); // per-row error maps
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const lastAddedRef = useRef(null);

  const recomputeDue = (r) => {
    const total = parseNum(r.total);
    const paid = parseNum(r.paid);
    return clampNum(total - paid);
  };

  const validateRow = (r) => {
    const e = {};
    const total = parseNum(r.total);
    const paid = parseNum(r.paid);
    const anyMoney = hasValue(r.total) || hasValue(r.paid);
    const anyContent =
      anyMoney ||
      hasValue(r.procedure) ||
      hasValue(r.visitDate) ||
      hasValue(r.nextApptDate);

    if (anyContent && !hasValue(r.visitDate)) e.visitDate = "Visit date is required";
    if (hasValue(r.total) && total < 0) e.total = "Total must be ≥ 0";
    if (hasValue(r.paid) && paid < 0) e.paid = "Paid must be ≥ 0";
    if (paid > total) e.paid = "Paid exceeds Total";
    if (r.nextApptDate && r.visitDate && r.nextApptDate < r.visitDate)
      e.nextApptDate = "Next appointment can't be before visit";
    return e;
  };

  const validateAll = (rs = rows) => rs.map(validateRow);

  useEffect(() => {
    setErrors(validateAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => {
      const copy = prev.map((r) => ({ ...r }));
      copy[idx][field] = value;
      if (field === "total" || field === "paid") {
        copy[idx].due = recomputeDue(copy[idx]);
      }
      return copy;
    });
  };

  const addRow = () => {
    setRows((r) => [...r, emptyRow()]);
    setTimeout(() => lastAddedRef.current?.focus(), 0);
  };

  const removeRow = (idx) =>
    setRows((r) => (r.length === 1 ? r : r.filter((_, i) => i !== idx)));

  const clearAll = () => setRows([emptyRow()]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, r) => s + parseNum(r.total), 0);
    const paid = rows.reduce((s, r) => s + parseNum(r.paid), 0);
    const due = clampNum(total - paid);
    return { total, paid, due };
  }, [rows]);

  const payload = useMemo(() => ({ rows, summary }), [rows, summary]);

  // ---- Autosave (debounced) ----
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        }
      } catch {}
    }, 800);
    return () => clearTimeout(id);
  }, [payload, DRAFT_KEY, open]);

  // ---- Fetch existing data and restore draft ----
  useEffect(() => {
    if (!open || !patientId) return;
    
    const fetchData = async () => {
      try {
        // First try to fetch from backend
        const data = await apiRequest(`get_procedure_tracking.php?patient_id=${patientId}`);
        if (data.success && data.tracking && data.tracking.procedures && data.tracking.procedures.length > 0) {
          const restored = data.tracking.procedures.map((r) => ({
            visitDate: r.visitDate ?? "",
            procedure: r.procedure ?? "",
            nextApptDate: r.nextApptDate ?? "",
            total: r.total?.toString() ?? "",
            paid: r.paid?.toString() ?? "",
            due: recomputeDue(r),
          }));
          setRows(restored);
          return;
        }
        // No data found - reset to defaults
        console.log('ProcedureTrackingForm: No existing data found, resetting to defaults');
        setRows([emptyRow()]);
        setErrors([]);
        setSubmitError("");
        // Clear localStorage draft for this patient
        if (typeof window !== "undefined") {
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch (error) {
        console.error('Error fetching procedure tracking:', error);
        // On error, reset to defaults
        setRows([emptyRow()]);
        setErrors([]);
        setSubmitError("");
      }
      
      // Don't use localStorage draft - always start fresh if no backend data
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setRows([emptyRow()]);
      setErrors([]);
      setSubmitError("");
    }
  }, [open]);

  const handleSaveDraft = async () => {
    if (!patientId) {
      toast({
        title: 'Error',
        description: 'Patient ID is missing',
        variant: 'destructive'
      });
      return;
    }

    try {
      const cleaned = buildCleanRows(rows);
      const networkPayload = {
        procedures: cleaned,
        summary: {
          total: cleaned.reduce((s, r) => s + r.total, 0),
          paid: cleaned.reduce((s, r) => s + r.paid, 0),
          due: clampNum(
            cleaned.reduce((s, r) => s + r.total, 0) -
            cleaned.reduce((s, r) => s + r.paid, 0)
          ),
        },
      };

      const data = await apiRequest('add_procedure_tracking.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          procedures: cleaned,
          summary: networkPayload.summary,
          is_draft: true
        })
      });

      if (data.success) {
        onSave?.(networkPayload);
        toast({
          title: 'Draft Saved',
          description: 'Your progress has been saved.',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to save draft',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save draft',
        variant: 'destructive'
      });
    }
  };

  // Build clean rows for the final submit (filter out empty ones, coerce numbers)
  const buildCleanRows = (rs) =>
    rs
      .filter((r) => {
        const anyMoney = hasValue(r.total) || hasValue(r.paid);
        const anyContent =
          anyMoney ||
          hasValue(r.procedure) ||
          hasValue(r.visitDate) ||
          hasValue(r.nextApptDate);
        return anyContent;
      })
      .map((r) => {
        const total = parseNum(r.total);
        const paid = parseNum(r.paid);
        return {
          visitDate: r.visitDate || null,
          procedure: String(r.procedure || "").trim(),
          nextApptDate: r.nextApptDate || null,
          total,
          paid,
          due: clampNum(total - paid),
        };
      });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    // Full validation
    const emap = validateAll();
    setErrors(emap);
    const hasAnyError = emap.some((m) => Object.keys(m).length);
    if (hasAnyError) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form',
        variant: 'destructive'
      });
      return;
    }

    // Require at least one meaningful row
    const cleaned = buildCleanRows(rows);
    if (!cleaned.length) {
      setSubmitError("Please add at least one procedure or payment row.");
      toast({
        title: 'Error',
        description: 'Please add at least one procedure or payment row.',
        variant: 'destructive'
      });
      return;
    }

    // Build the network-shaped payload
    const networkPayload = {
      procedures: cleaned,
      summary: {
        total: cleaned.reduce((s, r) => s + r.total, 0),
        paid: cleaned.reduce((s, r) => s + r.paid, 0),
        due: clampNum(
          cleaned.reduce((s, r) => s + r.total, 0) -
          cleaned.reduce((s, r) => s + r.paid, 0)
        ),
      },
    };

    setSaving(true);
    try {
      if (!patientId) {
        throw new Error('Patient ID is missing');
      }

      // Save to backend
      const data = await apiRequest('add_procedure_tracking.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          procedures: cleaned,
          summary: networkPayload.summary,
          is_draft: false
        })
      });

      if (data.success) {
        // Clear draft now that we're moving on
        try {
          if (typeof window !== "undefined") localStorage.removeItem(DRAFT_KEY);
        } catch {}

        onSave?.(networkPayload);
        
        // Store procedures data for Review form
        const proceduresData = {
          rows: cleaned,
          summary: networkPayload.summary
        };
        
        // If hideDialog is true, we're in a wizard - call onNext with data
        if (isHideDialog && onNext) {
          console.log('ProcedureTrackingForm: Calling onNext with proceduresData:', proceduresData);
          onNext(proceduresData);
        } else {
          // Close all modals and show Review page
          onClose();
          if (onNext) {
            onNext({
              type: 'showReview',
              patientId,
              patientName,
              medicalHistory,
              dentalExamination,
              procedures: proceduresData
            });
          }
        }
        
        toast({
          title: 'Success',
          description: 'Procedure tracking saved successfully',
          variant: 'default'
        });
      } else {
        throw new Error(data.error || 'Failed to save procedure tracking');
      }
    } catch (err) {
      setSubmitError(err?.message || "Failed to continue");
      toast({
        title: 'Error',
        description: err?.message || 'Failed to continue',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = errors.some((m) => Object.keys(m).length);

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error banner */}
          {submitError && (
            <div className="rounded-md bg-red-900/50 border border-red-500/50 p-3 text-sm text-red-300">
              {submitError}
            </div>
          )}

          {/* Table header - Desktop */}
          <div className="hidden md:grid grid-cols-12 gap-4 text-sm font-medium text-gray-400 mb-3 px-2">
            <div className="col-span-3">Visit Date</div>
            <div className="col-span-3">Next Appointment</div>
            <div className="col-span-3">Procedure</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1 text-right">Paid</div>
            <div className="col-span-1 text-right">Due</div>
          </div>

          {/* Rows */}
          <div className="space-y-4">
            {rows.map((row, idx) => {
              const err = errors[idx] || {};
              return (
                <div
                  key={idx}
                  className="space-y-4 p-4 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-800 transition-colors"
                >
                  {/* Date Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Visit Date */}
                    <div>
                      <Label className="text-white text-sm mb-2">Visit Date</Label>
                      <Input
                        ref={idx === rows.length - 1 ? lastAddedRef : undefined}
                        type="date"
                        value={row.visitDate}
                        min={minDate}
                        max={maxDate}
                        onChange={(e) => updateRow(idx, "visitDate", e.target.value)}
                        className={`w-full bg-gray-800 border-gray-600 text-white ${
                          err.visitDate ? "border-red-500" : ""
                        }`}
                      />
                      {err.visitDate && (
                        <p className="mt-1 text-xs text-red-400">{err.visitDate}</p>
                      )}
                    </div>

                    {/* Next Appointment */}
                    <div>
                      <Label className="text-white text-sm mb-2">Next Appointment</Label>
                      <Input
                        type="date"
                        value={row.nextApptDate}
                        min={row.visitDate || minDate}
                        max={maxDate}
                        onChange={(e) => updateRow(idx, "nextApptDate", e.target.value)}
                        className={`w-full bg-gray-800 border-gray-600 text-white ${
                          err.nextApptDate ? "border-red-500" : ""
                        }`}
                      />
                      {err.nextApptDate && (
                        <p className="mt-1 text-xs text-red-400">{err.nextApptDate}</p>
                      )}
                    </div>
                  </div>

                  {/* Procedure */}
                  <div>
                    <Label className="text-white text-sm mb-2">Procedure</Label>
                    <Input
                      value={row.procedure}
                      onChange={(e) => updateRow(idx, "procedure", e.target.value)}
                      placeholder="e.g., Composite filling 36"
                      className="w-full bg-gray-800 border-gray-600 text-white"
                    />
                  </div>

                  {/* Payment Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Total */}
                    <div>
                      <Label className="text-white text-sm mb-2">Total</Label>
                      <Input
                        inputMode="decimal"
                        placeholder="0"
                        value={row.total}
                        onChange={(e) => updateRow(idx, "total", e.target.value)}
                        className={`w-full bg-gray-800 border-gray-600 text-white text-right ${
                          err.total ? "border-red-500" : ""
                        }`}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">{inr(parseNum(row.total))}</p>
                      {err.total && (
                        <p className="mt-1 text-xs text-red-400">{err.total}</p>
                      )}
                    </div>

                    {/* Paid */}
                    <div>
                      <Label className="text-white text-sm mb-2">Paid</Label>
                      <Input
                        inputMode="decimal"
                        placeholder="0"
                        value={row.paid}
                        onChange={(e) => updateRow(idx, "paid", e.target.value)}
                        className={`w-full bg-gray-800 border-gray-600 text-white text-right ${
                          err.paid ? "border-red-500" : ""
                        }`}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">{inr(parseNum(row.paid))}</p>
                      {err.paid && (
                        <p className="mt-1 text-xs text-red-400">{err.paid}</p>
                      )}
                    </div>

                    {/* Due (read-only) */}
                    <div>
                      <Label className="text-white text-sm mb-2">Due</Label>
                      <div
                        className={`text-right font-medium rounded-lg px-3 py-2 ${
                          row.due > 0
                            ? "bg-red-900/30 text-red-400 border border-red-500/50"
                            : "bg-green-900/30 text-green-400 border border-green-500/50"
                        }`}
                      >
                        {inr(row.due)}
                      </div>
                    </div>
                  </div>

                  {/* Actions per row */}
                  <div className="flex justify-end gap-2 pt-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addRow}
                      className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Row
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      disabled={rows.length === 1}
                      className="bg-gray-800 border-gray-600 text-red-400 hover:bg-gray-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearAll}
                      className="bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-6 p-4 rounded-lg bg-gray-800/50 border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex flex-col">
                <span className="text-gray-400 text-xs mb-1">Total Amount</span>
                <span className="font-semibold text-white text-lg">{inr(summary.total)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-400 text-xs mb-1">Amount Paid</span>
                <span className="font-semibold text-green-400 text-lg">{inr(summary.paid)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-400 text-xs mb-1">Balance Due</span>
                <span
                  className={`font-semibold text-lg ${
                    summary.due > 0 ? "text-red-400" : "text-green-400"
                  }`}
                >
                  {inr(summary.due)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (onBack) onBack();
                onClose();
              }}
              className="w-full sm:w-auto bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
            >
              Back
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                className="flex-1 sm:flex-none bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
              >
                Save Draft
              </Button>
              <Button
                type="submit"
                disabled={hasErrors || saving}
                className="flex-1 sm:flex-none medical-gradient text-white hover:opacity-90"
              >
                {saving ? "Saving..." : "Next"}
              </Button>
            </div>
          </DialogFooter>
        </form>
  );

  // If hideDialog is true, return only form content (for wizard)
  if (isHideDialog) {
    return formContent;
  }

  // Otherwise, return with Dialog wrapper
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-6xl max-h-[95vh] overflow-y-auto w-[98vw]">
        <DialogHeader>
          <DialogTitle className="text-white text-xl sm:text-2xl">Procedure Tracking & Payments</DialogTitle>
          <DialogDescription className="text-gray-400">
            Track dental procedures and payment details
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default ProcedureTrackingForm;

